<template>
  <div class="vue-card-stack__wrapper">
    <teleport to="body">
      <div v-if="showClone" class="card-clone" :style="cloneStyle" @transitionend="onTransitionEnd">
        <div class="background-glow pulsing-glow" :style="{ '--glow-color': expandedData.backgroundColor }"></div>

        <v-card :style="cloneCardStyle" class="d-flex flex-column glass-card">
          <v-btn icon @click.stop="closeClone" class="clone-close-btn" dark><v-icon>mdi-close</v-icon></v-btn>
          <v-card-text class="flex-grow-1 overflow-y-auto pa-0">
            <div v-if="expandedData.summary" class="hero-header" :style="exerciseHeroStyle">
              <div class="hero-content text-center">
                <h3 class="font-weight-bold display-1 white--text">{{ expandedData.exercise.title }}</h3>
                <p class="text-subtitle-1 mt-1 white--text text-uppercase" style="opacity: 0.8; letter-spacing: 1.5px">
                  {{ getMainMuscleGroup(expandedData.exercise) }}
                </p>
              </div>
            </div>
            <div
              v-if="
                (expandedData.execution && !expandedData.execution.hyroxSegments) ||
                (expandedData.execution && expandedData.execution.hyroxSegments && !expandedData.execution.hyroxSegments.length)
              "
              class="hero-header"
              :style="sessionHeroStyle">
              <div class="hero-content">
                <div class="d-flex align-center justify-center">
                  <h3 class="font-weight-bold display-1 white--text">{{ expandedData.execution.title }}</h3>
                  <v-btn icon @click="openEditDialog" color="blue" class="ml-2" large><v-icon>mdi-pencil</v-icon></v-btn>
                </div>
                <p class="text-subtitle-1 mt-1 white--text" style="opacity: 0.8">
                  {{
                    expandedData.execution.ts
                      ? $moment.unix(expandedData.execution.ts).format("dd, DD. MMMM YY")
                      : $moment(expandedData.execution.createdAt).format("dd, DD. MMMM YY")
                  }}
                </p>
              </div>
            </div>

            <div
              class="content-wrapper"
              :class="{
                'pa-4':
                  expandedData.summary ||
                  (expandedData.execution && !expandedData.execution.hyroxSegments) ||
                  (expandedData.execution && !expandedData.execution.hyroxSegments.length),
              }">
              <div v-if="expandedData.summary">
                <v-row>
                  <v-col cols="12" md="5" class="animated-item" style="--delay: 0.1s">
                    <h4 class="font-weight-bold mb-4 white--text">Deine Bestleistungen</h4>

                    <div v-if="!isDurationExercise(expandedData)">
                      <div class="personal-best-card mb-4">
                        <div class="best-card-icon trophy"><v-icon large color="#FFD700">mdi-trophy-variant</v-icon></div>
                        <div>
                          <span class="font-weight-bold white--text">Maximales Gewicht</span>
                          <p class="text-h4 font-weight-bold mb-0 mt-1 white--text">
                            {{ expandedData.summary.maxWeightStep.weight }} kg x {{ expandedData.summary.maxWeightStep.reps }}
                          </p>
                          <span class="text-caption text-muted">{{ $moment(expandedData.summary.maxWeightStep.date).fromNow() }}</span>
                        </div>
                      </div>
                      <div class="personal-best-card" v-if="expandedData.summary.maxVolumeStep">
                        <div class="best-card-icon volume"><v-icon large color="#81D4FA">mdi-dumbbell</v-icon></div>
                        <div>
                          <span class="font-weight-bold white--text">Maximales Volumen</span>
                          <p class="text-h4 font-weight-bold mb-0 mt-1 white--text">
                            {{ expandedData.summary.maxVolumeStep.weight * expandedData.summary.maxVolumeStep.reps }} kg
                          </p>
                          <span class="text-caption text-muted">
                            {{ expandedData.summary.maxVolumeStep.weight }}kg x {{ expandedData.summary.maxVolumeStep.reps }} Wdh
                          </span>
                        </div>
                      </div>
                    </div>
                    <div v-if="estimatedOneRepMax" class="personal-best-card mt-4">
                      <div class="best-card-icon e1rm">
                        <v-icon large color="#FF8A80">mdi-calculator-variant</v-icon>
                      </div>
                      <div>
                        <span class="font-weight-bold white--text">Geschätzer 1 Rep Max</span>
                        <p class="text-h4 font-weight-bold mb-0 mt-1 white--text">{{ estimatedOneRepMax.value }} kg</p>
                        <span class="text-caption text-muted"> Basiert auf: {{ estimatedOneRepMax.fromSet }} </span>
                      </div>
                    </div>
                    <div v-if="isDurationExercise(expandedData)">
                      <div class="personal-best-card mb-4" v-if="recordDurationStep">
                        <div class="best-card-icon volume">
                          <v-icon large color="#81D4FA">mdi-clock-time-eight-outline</v-icon>
                        </div>
                        <div>
                          <span class="font-weight-bold white--text">Längste Einheit</span>
                          <p class="text-h4 font-weight-bold mb-0 mt-1 white--text">
                            {{ formatDuration(parseDurationToSeconds(recordDurationStep.time)) }}
                          </p>
                          <span class="text-caption text-muted">{{ $moment(recordDurationStep.date).fromNow() }}</span>
                        </div>
                      </div>

                      <div class="personal-best-card">
                        <div class="best-card-icon trophy">
                          <v-icon large color="#FFD700">mdi-history</v-icon>
                        </div>
                        <div>
                          <span class="font-weight-bold white--text">Gesamtdauer</span>
                          <p class="text-h4 font-weight-bold mb-0 mt-1 white--text">
                            {{ formatFriendlyDuration(expandedData.summary.totalDuration) }}
                          </p>
                          <span class="text-caption text-muted">Über {{ expandedData.sets.length }} Training(s)</span>
                        </div>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="7" class="animated-item" style="--delay: 0.2s">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <h4 class="font-weight-bold white--text">Fortschritt</h4>
                      <div class="chart-nav" @click="openDatePicker">
                        <span class="chart-period-label">{{ chartPeriodLabel }}</span>
                        <v-icon small class="ml-1">mdi-calendar</v-icon>
                      </div>
                    </div>
                    <apexchart
                      v-if="chartData"
                      ref="progressChart"
                      class="mt-2 chart-container"
                      type="area"
                      height="350"
                      :options="chartOptions"
                      :series="chartData['weight']"></apexchart>
                    <p v-else class="text-center text-muted mt-10">Nicht genügend Daten für diesen Zeitraum.</p>
                  </v-col>
                </v-row>
              </div>

              <div v-else-if="expandedData.execution">
                <template v-if="expandedData.execution && expandedData.execution.hyroxSegments && expandedData.execution.hyroxSegments.length > 0">
                  <div class="hyrox-summary-hero" :style="sessionHeroStyle">
                    <div class="hero-content text-center">
                      <h1 class="hyrox-title">RACE FINISHED</h1>
                      <div class="total-time-display">{{ formatDuration(expandedData.execution.duration) }}</div>
                      <div class="hyrox-category-display">
                        <v-icon v-if="hyroxCategoryDetails.icon">{{ hyroxCategoryDetails.icon }}</v-icon>
                        <span>{{ hyroxCategoryDetails.text }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="content-wrapper pa-4">
                    <h2 class="font-weight-bold headline mb-4 white--text">Segment Analysis</h2>
                    <v-list v-if="expandedData.execution.hyroxSegments" dark color="transparent" class="segment-list">
                      <template v-for="(segment, index) in expandedData.execution.hyroxSegments">
                        <v-list-item class="segment-item" @click="toggleSegment(index)">
                          <v-list-item-avatar tile size="50" class="mr-4 rounded-lg">
                            <v-img :src="getImageForExercise(getExercise(segment.exercise_id))"></v-img>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title class="segment-title">{{ segment.title }}</v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-action class="text-right">
                            <div class="segment-time">{{ formatDuration(segment.duration, true) }}</div>
                          </v-list-item-action>
                          <v-list-item-action class="ml-4">
                            <v-icon :class="{ 'rotate-icon': expandedSegmentIndex === index }">mdi-chevron-down</v-icon>
                          </v-list-item-action>
                        </v-list-item>

                        <v-expand-transition :key="`expand-${index}`">
                          <div v-if="expandedSegmentIndex === index" class="segment-history-wrapper">
                            <div v-if="getSegmentHistory(segment).length > 0">
                              <v-simple-table class="segment-history-table">
                                <thead>
                                  <tr>
                                    <th class="text-left">Datum</th>
                                    <th class="text-right">Zeit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="(item, i) in getSegmentHistory(segment)" :key="i">
                                    <td>{{ item.date }}</td>
                                    <td class="text-right">{{ item.time }}</td>
                                  </tr>
                                </tbody>
                              </v-simple-table>
                            </div>
                            <div v-else class="no-history-text text-center pa-4">
                              <v-icon color="grey">mdi-history</v-icon>
                              <p class="mt-2 grey--text">Dies ist deine erste aufgezeichnete Zeit für dieses Segment.</p>
                            </div>
                          </div>
                        </v-expand-transition>
                      </template>
                    </v-list>
                  </div>
                </template>
                <template v-else>
                  <v-row>
                    <v-col cols="12" class="animated-item" style="--delay: 0.1s">
                      <v-row>
                        <v-col cols="4" class="pr-1">
                          <div class="stat-card-small">
                            <div class="stat-emoji-small">⏱️</div>
                            <div class="text-center">
                              <div class="stat-value-small">{{ formatDuration(expandedData.execution.duration) }}</div>
                              <div class="stat-label-small">Dauer</div>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="4" class="px-1">
                          <div class="stat-card-small">
                            <div class="stat-emoji-small">🏋️‍♂️</div>
                            <div class="text-center">
                              <div class="stat-value-small">{{ Math.round(expandedData.execution.liftedWeight) || 0 }}</div>
                              <div class="stat-label-small">Volumen (kg)</div>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="4" class="pl-1">
                          <div class="stat-card-small">
                            <div class="stat-emoji-small">🔥</div>
                            <div class="text-center">
                              <div class="stat-value-small">{{ expandedData.execution.caloriesBurned }}</div>
                              <div class="stat-label-small">Kalorien</div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col
                      cols="12"
                      class="animated-item"
                      style="--delay: 0.2s"
                      v-if="expandedData.execution.records && expandedData.execution.records.length">
                      <h4 class="font-weight-bold mt-8 mb-4 white--text">🏆 Rekorde dieser Session</h4>
                      <v-slide-group dark>
                        <v-slide-item v-for="(pr, index) in expandedData.execution.records" :key="index">
                          <v-card class="pr-card my-2 mr-2">
                            <div class="pr-title">{{ pr.exerciseTitle }}</div>
                            <div class="pr-type">{{ pr.type }}</div>
                            <div class="pr-values">
                              <span class="old-value">{{ pr.oldValue }}</span>
                              <span class="mx-2">→</span>
                              <span class="new-value">{{ pr.newValue }}</span>
                            </div>
                          </v-card>
                        </v-slide-item>
                      </v-slide-group>
                    </v-col>
                    <v-col cols="12" class="animated-item" style="--delay: 0.3s">
                      <h4 class="font-weight-bold mt-8 mb-4 white--text">Ausgeführte Übungen</h4>
                      <v-expansion-panels flat dark class="custom-panels">
                        <v-expansion-panel v-for="exercise in processedSessionExercises" :key="exercise.id">
                          <v-expansion-panel-header>
                            <v-row align="center" no-gutters>
                              <v-col cols="auto" class="mr-4">
                                <div v-if="exercise.isSuperset" class="superset-avatar-stack">
                                  <v-avatar
                                    v-for="(subEx, subIndex) in exercise.exercise.exercises.slice(0, 2)"
                                    :key="subEx._id"
                                    rounded
                                    :size="40"
                                    color="rgba(255, 255, 255, 0.1)"
                                    :style="{ zIndex: 2 - subIndex, marginLeft: subIndex > 0 ? '-16px' : '0' }">
                                    <v-img :src="getImageForExercise(subEx)" position="top center"></v-img>
                                  </v-avatar>
                                </div>
                                <v-avatar v-else rounded size="56" color="rgba(255, 255, 255, 0.1)">
                                  <v-img :src="getImageForExercise(exercise.exercise)" position="top center"></v-img>
                                </v-avatar>
                              </v-col>
                              <v-col>
                                <div v-if="exercise.isSuperset">
                                  <div class="d-flex align-center">
                                    <v-chip x-small color="orange" outlined label class="mr-2">Supersatz</v-chip>
                                    <p class="font-weight-bold white--text mb-0 text-subtitle-1">
                                      {{ exercise.title }}
                                    </p>
                                  </div>
                                  <span class="text-caption text-muted">
                                    {{ exercise.exercise.exercises.map((e) => e.title).join(" / ") }}
                                  </span>
                                </div>
                                <div v-else>
                                  <p class="font-weight-bold white--text mb-0 text-subtitle-1">
                                    {{ exercise.title }}
                                  </p>
                                  <span class="text-caption text-muted">
                                    {{ exercise.sets.length }} Sätze ・ {{ exercise.totalWeight }} kg Volumen
                                  </span>
                                </div>
                              </v-col>
                            </v-row>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <v-simple-table dense class="mt-2 transparent-table">
                              <thead>
                                <tr>
                                  <th class="text-left">Satz</th>
                                  <th v-if="exercise.isSuperset" class="text-left">Übung</th>
                                  <th class="text-left">Gewicht</th>
                                  <th class="text-left">Wdh.</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="step in exercise.sets" :key="step._id">
                                  <td>{{ step.set }}</td>
                                  <td v-if="exercise.isSuperset">
                                    {{ getSubExerciseForSet(exercise.exercise, step.set).title }}
                                  </td>
                                  <td>{{ step.weight }} kg</td>
                                  <td>{{ step.reps }}</td>
                                </tr>
                              </tbody>
                            </v-simple-table>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                    <v-col class="text-center">
                      <v-btn @click="deleteSession()" color="red" class="text-center" outlined>Training löschen</v-btn>
                    </v-col>
                  </v-row>
                </template>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </teleport>

    <v-dialog style="z-index: 100000" v-model="showDatePicker" max-width="320" dark>
      <v-card class="rounded-xl">
        <v-date-picker v-model="customDateRange" range full-width no-title color="blue" header-color="primary" event-color="blue"></v-date-picker>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDatePicker">Abbrechen</v-btn>
          <v-btn color="primary" text @click="applyDateRange">Anwenden</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog style="z-index: 100000" v-model="showDeleteConfirmationDialog" max-width="380" content-class="delete-dialog-wrapper" persistent>
      <transition name="dialog-scale" appear>
        <v-card v-if="showDeleteConfirmationDialog" class="delete-dialog-card glass-card">
          <div class="delete-dialog-glow" :style="{ '--glow-color': expandedData.backgroundColor }"></div>

          <v-card-title class="delete-dialog-title red--text text-center font-weight-bold justify-center">
            <v-icon large color="red" class="mr-3">mdi-alert-outline</v-icon>
            Training wirklich löschen?
          </v-card-title>

          <v-card-text class="text-center white--text mt-2">
            Diese Aktion kann nicht rückgängig gemacht werden. Deine Aufzeichnungen für diese Session gehen verloren.
          </v-card-text>

          <v-card-actions class="pa-4 pt-3">
            <v-spacer></v-spacer>
            <v-btn color="white" text @click="cancelDeletion"> Abbrechen </v-btn>
            <v-btn class="ml-2" depressed color="red" @click="confirmDeletion" :loading="isDeleting"> Löschen </v-btn>
          </v-card-actions>
        </v-card>
      </transition>
    </v-dialog>

    <div v-if="showDeleteSuccess" class="success-overlay">
      <transition name="success-pop" appear>
        <div class="success-content">
          <div class="success-icon-wrapper">
            <v-icon size="60" color="white">mdi-check</v-icon>
          </div>
          <p class="mt-4 text-h5 font-weight-bold">Erfolgreich gelöscht</p>
        </div>
      </transition>
    </div>

    <div v-if="ghostCard" class="vue-card-stack__card" :style="getCardStyle(ghostCard)">
      <div class="card-content-wrapper" :style="{ '--glow-color': ghostCard.backgroundColor }">
        <v-card dark class="rounded-xl fill-height" :style="getCardBackground(ghostCard, ghostCard._index)">
          <div v-if="exercises" class="d-flex flex-column pa-5 fill-height">
            <v-card-title class="font-weight-bold white--text pa-0 text-h5 text-on-image">
              {{ ghostCard.exercise.title }}
            </v-card-title>
          </div>
          <div v-else class="d-flex flex-column pa-5 fill-height">
            <v-card-title class="font-weight-bold white--text pa-0 text-h5">
              {{ ghostCard.execution.title }}
            </v-card-title>
          </div>
        </v-card>
      </div>
    </div>
    <div class="vue-card-stack__stack" :style="{ height: containerHeight, width: containerWidth }">
      <div
        class="vue-card-stack__card"
        v-for="(card, cardIndex) in stack.slice(0, _maxVisibleCards)"
        :key="card._id"
        :style="getCardStyle(card)"
        @click="!isDragging && expandCard(cardIndex, $event)">
        <div class="card-content-wrapper" :class="{ 'is-top-card': cardIndex === 0 }" :style="{ '--glow-color': card.backgroundColor }">
          <v-card dark class="rounded-xl fill-height card-shine-effect" :style="getCardBackground(card, cardIndex)">
            <div v-if="exercises" class="d-flex flex-column pa-5 fill-height">
              <v-card-title class="font-weight-bold white--text pa-0 text-h5 text-on-image">
                {{ card.exercise.title }}
              </v-card-title>
              <v-spacer></v-spacer>
              <v-card-text class="pa-0 text-on-image" v-if="card.summary">
                <v-row no-gutters>
                  <v-col class="text-center">
                    <v-icon>mdi-trophy-award</v-icon>
                    <div class="font-weight-bold text-subtitle-1">{{ card.summary.maxWeightStep.weight }} kg</div>
                  </v-col>
                  <v-col class="text-center">
                    <v-icon>mdi-fire</v-icon>
                    <div class="font-weight-bold text-subtitle-1">{{ card.summary.maxVolumeStep.weight * card.summary.maxVolumeStep.reps }} kg</div>
                  </v-col>
                </v-row>
              </v-card-text>
            </div>

            <div v-else class="d-flex flex-column pa-5 fill-height">
              <v-row no-gutters>
                <v-col cols="9">
                  <v-card-title class="font-weight-bold white--text pa-0 text-h5">
                    {{ card.execution.title }}
                  </v-card-title>
                  <v-card-subtitle class="pa-0 mt-1">
                    {{ $moment(card.execution.createdAt).format("dd, DD. MMM YY") }}
                  </v-card-subtitle>
                </v-col>
              </v-row>
              <v-spacer></v-spacer>
              <v-card-text class="pa-0">
                <v-row no-gutters>
                  <v-col class="text-center">
                    <v-icon large>mdi-clock-time-eight</v-icon>
                    <div class="font-weight-bold text-subtitle-1">{{ formatDuration(card.execution.duration) }}</div>
                  </v-col>
                  <v-col class="text-center">
                    <v-icon large>mdi-weight-lifter</v-icon>
                    <div class="font-weight-bold text-subtitle-1">{{ Math.round(card.execution.liftedWeight) || 0 }} kg</div>
                  </v-col>
                  <v-col class="text-center">
                    <v-icon large>mdi-fire</v-icon>
                    <div class="font-weight-bold text-subtitle-1">{{ card.execution.caloriesBurned }} kcal</div>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-img :src="card.img" contain height="70" width="70" class="verlauf-card-icon"></v-img>
            </div>
          </v-card>
        </div>
      </div>
    </div>
    <v-dialog style="z-index: 9999" v-model="editDialog" max-width="400px" dark>
      <v-card class="pa-4 rounded-xl glass-card">
        <v-card-title>Training bearbeiten</v-card-title>
        <v-card-text>
          <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="editForm.date" label="Datum" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on"></v-text-field>
            </template>
            <v-date-picker v-model="editForm.date" @input="dateMenu = false"></v-date-picker>
          </v-menu>

          <v-text-field v-model="editForm.durationMinutes" label="Dauer (Minuten)" type="number" prepend-icon="mdi-clock-outline"></v-text-field>
          <v-text-field v-model="editForm.calories" label="Kalorien" type="number" prepend-icon="mdi-fire"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="saveSessionEdit" :loading="isSaving">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { debounce } from "./utils/debounce"
import { mapGetters, mapActions, mapState } from "vuex"
import MuscleDiagram from "../PlanRunInteractiveHuman.vue"
import MetricCard from "./metricCard.vue"
import api from "@/api"

export default {
  name: "TrainingsHubCardStack",
  components: { MuscleDiagram, MetricCard },
  props: {
    exercises: { type: Boolean, default: false },
    cards: { type: Array, default: () => [] },
    cardHeight: { type: Number, default: 250 },
    cardWidth: { default: "100%" },
    stackWidth: { type: [Number, String], default: null },
    sensitivity: { type: Number, default: 0.25 },
    maxVisibleCards: { type: Number, default: 3 },
    scaleMultiplier: { type: Number, default: 0.5 },
    speed: { type: Number, default: 0.2 },
    paddingHorizontal: { type: Number, default: 0 },
    paddingVertical: { type: Number, default: 20 },
  },
  data: () => ({
    editDialog: false,
    dateMenu: false,
    isSaving: false,
    editForm: {
      date: "",
      durationMinutes: 0,
      calories: 0,
    },
    expandedSegmentIndex: null,
    stack: [],
    width: 0,
    activeCardIndex: 0,
    isDragging: false,
    dragStartY: 0,
    isDraggingDown: false,
    showClone: false,
    isClosing: false,
    cloneStyle: {},
    cloneCardStyle: {},
    originalCloneStyle: {},
    expandedData: {},
    dragThreshold: 10,
    ghostCard: null,
    lastDragOffset: 0,
    showDatePicker: false,
    customDateRange: [],
    chartStartDate: null,
    chartEndDate: null,
    showDeleteConfirmationDialog: false,
    isDeleting: false,
    showDeleteSuccess: false,
  }),
  watch: {
    showClone(isShown) {
      if (isShown && this.expandedData.summary) {
        this.initializeDateRange()
      }
    },
  },
  mounted() {
    this.init()
    window.addEventListener("resize", this.handleResize)
    document.addEventListener(this.touchEndEvent, this.onTouchEnd)
    this.$el.addEventListener(this.touchStartEvent, this.onTouchStart)
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize)
    document.removeEventListener(this.touchEndEvent, this.onTouchEnd)
    this.$el.removeEventListener(this.touchStartEvent, this.onTouchStart)
    document.removeEventListener(this.dragEvent, this.onDrag)
  },
  computed: {
    ...mapState("runWorkoutStore", ["hubGroupedSessions"]),
    ...mapGetters("deviceStore", ["exerciseList"]),
    estimatedOneRepMax() {
      // Funktioniert nicht für Dauer- oder Distanzübungen
      if (!this.expandedData.sets || this.isDurationExercise(this.expandedData)) {
        return null
      }

      let max1RM = 0
      let bestSet = null

      // Gehe durch alle historischen Sets und alle Sätze (steps) darin
      this.expandedData.sets.forEach((set) => {
        if (set.steps) {
          set.steps.forEach((step) => {
            const weight = parseFloat(step.weight)
            const reps = parseInt(step.reps, 10)

            // Wir berechnen es nur für Sätze mit Gewicht und Reps
            if (weight > 0 && reps > 0) {
              const e1RM = this.calculateEpley1RM(weight, reps)
              if (e1RM > max1RM) {
                max1RM = e1RM
                bestSet = step
              }
            }
          })
        }
      })

      if (max1RM === 0 || !bestSet) return null

      return {
        value: Math.round(max1RM * 10) / 10, // Auf eine Dezimalstelle runden
        fromSet: `${bestSet.weight} kg x ${bestSet.reps} Wdh.`,
      }
    },
    // NEU: Diese computed property bereitet die Daten auf und prüft auf Supersätze
    processedSessionExercises() {
      if (!this.expandedData || !this.expandedData.sets) return []
      const exercisesMap = {}
      this.expandedData.sets.forEach((set) => {
        if (!set.steps) return
        let exerciseID = set.exercise_id

        let fullExercise = this.getExercise(exerciseID)

        // NEU: Wenn die Hauptübung (Superset) nicht gefunden wurde, versuchen wir sie aus den Steps zu rekonstruieren.
        // Das passiert, wenn das Superset-Objekt selbst gelöscht wurde, aber die "Kinder" noch existieren.
        if (!fullExercise && set.steps && set.steps.length > 0) {
          // Sammle alle eindeutigen subExerciseIds aus den Steps
          const subIds = [...new Set(set.steps.map((s) => s.subExerciseId).filter(Boolean))]
          for (let subID of subIds) {
            exerciseID += subID
          }
          if (subIds.length > 0) {
            // Versuche, die Unter-Übungen zu laden
            const subExercises = subIds.map((id) => this.getExercise(id)).filter(Boolean)

            if (subExercises.length > 0) {
              // Baue ein virtuelles Superset-Objekt
              fullExercise = {
                title: "Supersatz",
                isSuperset: true,
                exercises: subExercises,
                // Optional: Wir könnten hier noch Dummy-Werte für Tags etc. setzen
              }
            }
          }
        }

        set.steps.forEach((step) => {
          if (!exercisesMap[exerciseID]) {
            exercisesMap[exerciseID] = {
              id: exerciseID,
              sets: [],
              totalReps: 0,
              totalWeight: 0,
              exercise: fullExercise,
              isSuperset: fullExercise?.isSuperset ?? false,
              title: fullExercise?.title ?? "Unbekannte Übung",
            }
          }
          const exerciseGroup = exercisesMap[exerciseID]
          exerciseGroup.sets.push(step)
          exerciseGroup.totalReps += step.reps || 0
          exerciseGroup.totalWeight += (step.weight || 0) * (step.reps || 0)
        })
      })
      return Object.values(exercisesMap)
    },
    earliestSetDate() {
      if (!this.expandedData.sets || this.expandedData.sets.length === 0) {
        return this.$moment().subtract(3, "months") // Fallback
      }
      const earliestTimestamp = Math.min(...this.expandedData.sets.map((s) => new Date(s.date).getTime()))
      return this.$moment(earliestTimestamp)
    },
    hyroxCategoryDetails() {
      if (!this.expandedData.execution || !this.expandedData.execution.title) {
        return { icon: null, text: "" }
      }
      const title = this.expandedData.execution.title

      if (title.includes("Men Pro")) return { icon: "mdi-crown", text: "Men Pro" }
      if (title.includes("Women Pro")) return { icon: "mdi-crown-outline", text: "Women Pro" }
      if (title.includes("Men")) return { icon: "mdi-human-male", text: "Men" }
      if (title.includes("Women")) return { icon: "mdi-human-female", text: "Women" }
      if (title.includes("Üben")) return { icon: "mdi-dumbbell", text: "Freies Training" }

      return { icon: null, text: title.replace("HYROX ", "") }
    },
    recordDurationStep() {
      if (!this.isDurationExercise(this.expandedData)) return null
      let longestStep = null
      let maxDuration = 0

      if (!this.expandedData.sets) return null

      this.expandedData.sets.forEach((set) => {
        if (set.steps) {
          set.steps.forEach((step) => {
            const durationInSeconds = this.parseDurationToSeconds(step.time)
            if (durationInSeconds > maxDuration) {
              maxDuration = durationInSeconds
              // Fügt das Datum des Sets zum Step-Objekt hinzu
              longestStep = { ...step, date: set.date }
            }
          })
        }
      })
      return longestStep
    },
    latestSetDate() {
      if (!this.expandedData.sets || this.expandedData.sets.length === 0) {
        return this.$moment()
      }
      const latestTimestamp = Math.max(...this.expandedData.sets.map((s) => new Date(s.date).getTime()))
      return this.$moment(latestTimestamp)
    },
    exerciseHeroStyle() {
      if (!this.expandedData.exercise) return {}
      const imageUrl = this.getImageForExercise(this.expandedData.exercise)
      return {
        backgroundImage: `
                linear-gradient(to top, rgba(30, 30, 30, 1) 5%, rgba(30, 30, 30, 0.6) 50%, rgba(30,30,30,0.2) 100%),
                url(${imageUrl})
            `,
      }
    },
    sessionHeroStyle() {
      if (!this.expandedData.execution) return {}
      let viz = this.expandedData.execution.fixedVisualization || { color: this.expandedData.backgroundColor, img: this.expandedData.img }
      return {
        backgroundImage: `
                linear-gradient(to top, rgba(30, 30, 30, 1) 5%, rgba(30, 30, 30, 0.7) 40%, ${viz.color}B3 100%),
                url(${viz.img})
            `,
      }
    },
    chartPeriodLabel() {
      if (!this.chartStartDate || !this.chartEndDate) return "Zeitraum wählen"
      return `${this.$moment(this.chartStartDate).format("DD. MMM YYYY")} - ${this.$moment(this.chartEndDate).format("DD. MMM YYYY")}`
    },
    chartData() {
      if (!this.expandedData.summary || !this.chartStartDate || !this.chartEndDate) return null

      const setsInPeriod = this.expandedData.sets.filter((s) => {
        const setDate = this.$moment(s.date)
        return setDate.isBetween(this.chartStartDate, this.chartEndDate, "day", "[]")
      })

      if (setsInPeriod.length === 0) return null

      const dataByDay = {}
      setsInPeriod.forEach((s) => {
        if (s.steps) {
          const dayKey = this.$moment(s.date).format("YYYY-MM-DD")
          if (!dataByDay[dayKey]) dataByDay[dayKey] = []
          dataByDay[dayKey].push(...s.steps)
        }
      })

      const sortedDays = Object.keys(dataByDay).sort()
      const isDuration = this.isDurationExercise(this.expandedData)

      // Logik für Dauer-Übungen
      if (isDuration) {
        const durationData = sortedDays.map((day) => {
          const maxDuration = Math.max(0, ...dataByDay[day].map((step) => this.parseDurationToSeconds(step.time) || 0))
          return { x: day, y: maxDuration }
        })
        if (durationData.length < 2) return null
        return { duration: [{ name: "Max. Dauer", data: durationData }] }
      }
      // Logik für Gewicht-Übungen (bestehend)
      else {
        const weightData = sortedDays.map((day) => {
          const maxWeight = Math.max(0, ...dataByDay[day].map((step) => step.weight || 0))
          return { x: day, y: maxWeight }
        })
        if (weightData.length < 2) return null
        return { weight: [{ name: "Max. Gewicht", data: weightData }] }
      }
    },
    chartOptions() {
      if (this.expandedData.summary) {
        const isDuration = this.isDurationExercise(this.expandedData)

        // Prüfen, wie viele Datenpunkte wir haben (für Fein-Tuning)
        // Wir nehmen einfach die Länge des ersten Series-Arrays (falls vorhanden)
        const dataPointCount = this.chartData && this.chartData["weight"] && this.chartData["weight"][0] ? this.chartData["weight"][0].data.length : 0

        return {
          chart: {
            type: "area",
            height: 350,
            foreColor: "#9ca3af", // Etwas helleres Grau für bessere Lesbarkeit der Labels
            fontFamily: "Roboto, sans-serif",
            toolbar: { show: false },
            zoom: { enabled: false },
            animations: {
              enabled: true,
              easing: "easeinout",
              speed: 800,
            },
          },
          colors: ["#00E396"], // Dein Grünton
          dataLabels: { enabled: false },
          stroke: {
            curve: "monotoneCubic", // Viel organischerer Verlauf als "smooth"
            width: 3,
            dropShadow: {
              // Der "Neon-Glow" Effekt
              enabled: true,
              top: 0,
              left: 0,
              blur: 10,
              opacity: 0.5,
              color: "#00E396",
            },
          },
          // HIER IST DER FIX FÜR "HÄSSLICH":
          markers: {
            size: 0, // Keine Punkte im Normalzustand!
            colors: ["#1E1E1E"],
            strokeColors: "#00E396",
            strokeWidth: 3,
            hover: {
              size: 7, // Grosser Punkt nur beim Hovern
              sizeOffset: 3,
            },
          },
          fill: {
            type: "gradient",
            gradient: {
              shadeIntensity: 1,
              opacityFrom: 0.6,
              opacityTo: 0.05, // Stärkerer Fade nach unten
              stops: [0, 100],
            },
          },
          tooltip: {
            theme: "dark",
            style: {
              fontSize: "14px",
              fontFamily: "Roboto, sans-serif",
            },
            x: { format: "dd. MMM yyyy" }, // Jahr dazu, falls es lange her ist
            y: {
              formatter: (val) => {
                if (isDuration) {
                  if (val === 0) return "0s"
                  const minutes = Math.floor(val / 60)
                  const seconds = Math.floor(val % 60)
                  return `${minutes}m ${seconds}s`
                }
                return `${val.toFixed(1)} kg` // Eine Nachkommastelle sieht präziser aus
              },
            },
            marker: { show: false }, // Kein extra Marker im Tooltip, wir haben ja den auf der Linie
          },
          grid: {
            borderColor: "rgba(255, 255, 255, 0.08)", // Sehr subtil
            strokeDashArray: 4,
            xaxis: { lines: { show: false } }, // Vertikale Linien weg für cleaneren Look
            yaxis: { lines: { show: true } },
            padding: { top: 0, right: 10, bottom: 0, left: 10 },
          },
          xaxis: {
            type: "datetime",
            tooltip: { enabled: false }, // Tooltip oben an der Achse ausblenden (stört oft)
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
              style: { colors: "#6b7280", fontSize: "11px" },
              datetimeFormatter: {
                year: "yyyy",
                month: "MMM 'yy",
                day: "dd. MMM",
                hour: "HH:mm",
              },
            },
            crosshairs: {
              show: true, // Fadenkreuz beim Hovern
              position: "back",
              stroke: {
                color: "#00E396",
                width: 1,
                dashArray: 3,
              },
            },
            min: this.chartStartDate ? this.$moment(this.chartStartDate).valueOf() : undefined,
            max: this.chartEndDate ? this.$moment(this.chartEndDate).valueOf() : undefined,
          },
          yaxis: {
            tickAmount: 5, // Verhindert zu viele Linien
            labels: {
              style: { colors: "#6b7280", fontSize: "11px" },
              formatter: (val) => {
                if (isDuration) {
                  const minutes = Math.floor(val / 60)
                  const seconds = Math.floor(val % 60)
                  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
                }
                return val.toFixed(0)
              },
            },
          },
        }
      }
      return {}
    },
    // Veraltet, wird durch processedSessionExercises ersetzt
    sessionExercises() {
      if (!this.expandedData || !this.expandedData.sets) return []
      const exercises = {}
      this.expandedData.sets.forEach((set) => {
        if (!set.steps) return
        set.steps.forEach((step) => {
          if (!exercises[set.exercise_id]) {
            exercises[set.exercise_id] = { id: set.exercise_id, sets: [], totalReps: 0, totalWeight: 0 }
          }
          exercises[set.exercise_id].sets.push(step)
          exercises[set.exercise_id].totalReps += step.reps || 0
          exercises[set.exercise_id].totalWeight += (step.weight || 0) * (step.reps || 0)
        })
      })
      return Object.values(exercises)
    },
    containerHeight() {
      const stackOffset = (this._maxVisibleCards - 1) * this.overlapOffset
      return `${this.cardHeight + this.paddingVertical * 2 + stackOffset}px`
    },
    containerWidth() {
      if (!this.stackWidth) return `${this.cardWidth + this.paddingHorizontal * 2}px`
      if (typeof this.stackWidth === "number") return `${this.stackWidth}px`
      return this.stackWidth
    },
    _maxVisibleCards() {
      return this.maxVisibleCards > this.cards.length ? this.cards.length : this.maxVisibleCards
    },
    overlapOffset() {
      return 20
    },
    verticalStackRestPoints() {
      return this.cards.map((_, i) => this.paddingVertical + i * this.overlapOffset)
    },
    cardDefaults() {
      return this.cards.map((_, i) => {
        const baseY = this.verticalStackRestPoints[i]
        return {
          opacity: 1,
          display: "block",
          xPos: this.paddingHorizontal,
          yPos: baseY,
          scale: i === 0 ? 1 : 1 - this._scaleMultiplier,
          width: this.cardWidth,
          height: this.cardHeight,
          zIndex: this.cards.length - i,
          isDragging: this.isDragging,
        }
      })
    },
    _scaleMultiplier() {
      return 0.05
    },
    isTouch() {
      return "ontouchstart" in window
    },
    dragEvent() {
      return this.isTouch ? "touchmove" : "mousemove"
    },
    touchStartEvent() {
      return this.isTouch ? "touchstart" : "mousedown"
    },
    touchEndEvent() {
      return this.isTouch ? "touchend" : "mouseup"
    },
    originalActiveCardIndex() {
      return this.stack[this.activeCardIndex]?._index || 0
    },
  },
  methods: {
    ...mapActions("runWorkoutStore", ["setActiveSplit", "setGroupedExercises"]),
    openEditDialog() {
      const exec = this.expandedData.execution

      // WICHTIG: ts ist Unix-Timestamp (Sekunden).
      // Wir bevorzugen ts, nehmen createdAt nur als Fallback.
      let dateMoment
      if (exec.ts) {
        dateMoment = this.$moment.unix(exec.ts)
      } else {
        dateMoment = this.$moment(exec.createdAt)
      }

      this.editForm = {
        date: dateMoment.format("YYYY-MM-DD"), // Das Format für den Datepicker
        durationMinutes: Math.floor(exec.duration / 60),
        calories: exec.caloriesBurned,
      }
      this.editDialog = true
    },
    async saveSessionEdit() {
      this.isSaving = true
      try {
        const exec = this.expandedData.execution // Referenz auf die aktuelle Session

        const payload = {
          createdAt: this.$moment(this.editForm.date).toISOString(),
          duration: this.editForm.durationMinutes * 60,
          caloriesBurned: this.editForm.calories,
          // Wir senden den bestehenden Titel mit, damit er im Backend
          // nicht durch 'null' überschrieben wird
          title: exec.title,
        }

        // API Call zu deiner neuen Route
        await api.workoutApi.updateWorkoutExecution(this.expandedData.execution._id, payload)
        this.$toast.success("Training erfolgreich aktualisiert!")
        this.editDialog = false

        // Wichtig: Wir schliessen die Detailansicht und triggern einen Refresh,
        // damit die Timeline die neuen Daten (Datum/Dauer) sofort anzeigt.
        this.closeClone()
        this.$emit("session-deleted")
      } catch (e) {
        console.error("Fehler beim Speichern der Session:", e)
        this.$toast.error("Änderungen konnten nicht gespeichert werden.")
      } finally {
        this.isSaving = false
      }
    },
    calculateEpley1RM(weight, reps) {
      // Epley-Formel: 1RM = weight * (1 + (reps / 30))
      // Die Formel ist ungenau für sehr hohe Wdh-Zahlen, wir kappen sie z.B. bei 20
      if (!weight || !reps || reps > 20) return 0
      return weight * (1 + reps / 30)
    },
    // NEU: Hilfsfunktion, um die Unter-Übung für einen bestimmten Satz zu finden
    getSubExerciseForSet(supersetExercise, setNumber) {
      if (!supersetExercise || !supersetExercise.isSuperset || !Array.isArray(supersetExercise.exercises)) {
        return { title: "" } // Fallback
      }
      const subIndex = (setNumber - 1) % supersetExercise.exercises.length
      return supersetExercise.exercises[subIndex] || { title: "" }
    },
    toggleSegment(index) {
      this.expandedSegmentIndex = this.expandedSegmentIndex === index ? null : index
    },
    getSegmentHistory(segment) {
      // NEU: Greift auf "groupedSessions" aus dem Vuex Store zu
      if (!this.hubGroupedSessions || !segment) return []
      const history = []

      // 1. Finde alle vergangenen Sessions mit exakt demselben Titel
      const pastRaces = this.hubGroupedSessions.filter(
        (session) =>
          session.execution &&
          session.execution.title === this.expandedData.execution.title &&
          session.execution.hyroxSegments &&
          // Wichtig: Schliesst die aktuell angezeigte Session aus
          session.execution._id !== this.expandedData.execution._id,
      )

      // 2. Durchlaufe diese Rennen und extrahiere die Leistung für das spezifische Segment
      pastRaces.forEach((race) => {
        const pastSegment = race.execution.hyroxSegments.find((s) => s.segmentIndex === segment.segmentIndex)
        if (pastSegment && pastSegment.duration > 0) {
          history.push({
            date: this.$moment(parseInt(race.execution.ts, 10) * 1000).format("DD.MM.YYYY"),
            time: this.formatDuration(pastSegment.duration, true), // MM:SS Format
          })
        }
      })

      return history
    },
    deleteSession() {
      this.showDeleteConfirmationDialog = true
    },

    // Schritt 2: Löschen abbrechen
    cancelDeletion() {
      this.showDeleteConfirmationDialog = false
    },
    async confirmDeletion() {
      if (!this.expandedData.execution || !this.expandedData.execution._id) {
        return
      }

      this.isDeleting = true

      try {
        // API-Aufruf zum Löschen der Session
        await api.workoutApi.deleteWorkoutSession({
          id: this.expandedData.execution._id,
        })

        // API-Aufruf war erfolgreich
        this.showDeleteConfirmationDialog = false
        this.showDeleteSuccess = true

        // Nach 2 Sekunden Erfolgsmeldung ausblenden und Hauptansicht schliessen
        setTimeout(() => {
          this.showDeleteSuccess = false
          this.closeClone() // Bestehende Methode zum Schliessen der Detailansicht
          this.$emit("session-deleted") // Event für die Eltern-Komponente
        }, 2000)
      } catch (error) {
        // Optional: zeige hier eine Fehlermeldung an
        alert("Das Training konnte nicht gelöscht werden.")
        this.showDeleteConfirmationDialog = false
      } finally {
        this.isDeleting = false
      }
    },
    isDurationExercise(exercise) {
      if (!exercise || !exercise.summary) return false
      return exercise.summary.totalDuration > 0 && exercise.summary.totalWeight === 0
    },
    parseDurationToSeconds(timeString) {
      if (!timeString || typeof timeString !== "string") {
        return 0
      }

      // Teilt den String in Minuten und den Rest (z.B. ["45", "00.223"])
      const mainParts = timeString.split(":")
      if (mainParts.length !== 2) {
        return 0
      }

      // Teilt den zweiten Teil in Sekunden und Millisekunden (z.B. ["00", "223"])
      const secondParts = mainParts[1].split(".")
      if (secondParts.length !== 2) {
        return 0
      }

      const minutes = parseInt(mainParts[0], 10)
      const seconds = parseInt(secondParts[0], 10)
      const milliseconds = parseInt(secondParts[1], 10)

      // Sicherstellen, dass die Umwandlung in Zahlen erfolgreich war
      if (isNaN(minutes) || isNaN(seconds) || isNaN(milliseconds)) {
        return 0
      }

      // Ein Duration-Objekt aus den Einzelteilen erstellen und in Sekunden umwandeln
      const duration = this.$moment.duration({
        minutes: minutes,
        seconds: seconds,
        milliseconds: milliseconds,
      })

      return duration.asSeconds()
    },
    formatFriendlyDuration(totalSeconds) {
      if (isNaN(totalSeconds) || totalSeconds < 0) return "0 min"

      const duration = this.$moment.duration(totalSeconds, "seconds")
      const hours = Math.floor(duration.asHours())
      const minutes = duration.minutes()

      let result = ""
      if (hours > 0) {
        result += `${hours}h `
      }
      if (minutes > 0 || hours === 0) {
        result += `${minutes}min`
      }
      return result.trim() || "0 min"
    },
    initializeDateRange() {
      this.chartEndDate = this.latestSetDate.clone().endOf("day")
      this.chartStartDate = this.earliestSetDate.clone().startOf("day") // Geändert
      this.customDateRange = [this.chartStartDate.format("YYYY-MM-DD"), this.chartEndDate.format("YYYY-MM-DD")]
      this.refreshChart()
    },
    openDatePicker() {
      this.customDateRange = [this.$moment(this.chartStartDate).format("YYYY-MM-DD"), this.$moment(this.chartEndDate).format("YYYY-MM-DD")]
      this.showDatePicker = true
    },
    closeDatePicker() {
      this.showDatePicker = false
    },
    applyDateRange() {
      if (this.customDateRange.length === 2) {
        this.customDateRange.sort()
        this.chartStartDate = this.customDateRange[0]
        this.chartEndDate = this.customDateRange[1]
      }
      this.closeDatePicker()
      this.refreshChart() // NEU: Chart nach der Auswahl aktualisieren
    },
    // NEU: Diese Methode gibt dem Diagramm den nötigen "Anstoss"
    refreshChart() {
      this.$nextTick(() => {
        if (this.$refs.progressChart) {
          // `updateOptions` ist der sicherste Weg, um ein Re-Rendering mit neuen Daten zu erzwingen
          this.$refs.progressChart.updateOptions(this.chartOptions)
        }
      })
    },
    getMainMuscleGroup(ex) {
      if (!ex || !ex.tags) return "Ganzkörper"
      const primaryTag = ex.tags.find((t) => t.isPrimary)
      return primaryTag ? primaryTag.name : ex.tags[0]?.name || "Ganzkörper"
    },
    expandCardFromTarget(data, targetElement) {
      if (!targetElement) return
      const rect = targetElement.getBoundingClientRect()
      this.originalCloneStyle = {
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        borderRadius: getComputedStyle(targetElement).borderRadius || "28px",
      }
      this.expandedData = { ...data }
      this.cloneStyle = {
        position: "fixed",
        top: this.originalCloneStyle.top,
        left: this.originalCloneStyle.left,
        width: this.originalCloneStyle.width,
        height: this.originalCloneStyle.height,
        zIndex: 10000,
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        transformOrigin: "center",
      }
      this.cloneCardStyle = {
        backgroundColor: "transparent",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: this.originalCloneStyle.borderRadius,
      }
      this.showClone = true
      this.$nextTick(() => {
        Object.assign(this.cloneStyle, { top: "0", left: "0", width: "100vw", height: "100vh" })
        this.cloneCardStyle.borderRadius = "0"
      })
    },
    formatDuration(totalSeconds, minutesAndSeconds = false) {
      if (isNaN(totalSeconds) || totalSeconds < 0) return minutesAndSeconds ? "00:00" : totalSeconds

      const duration = this.$moment.duration(totalSeconds, "seconds")
      if (minutesAndSeconds) {
        const minutes = Math.floor(duration.asMinutes())
        const seconds = duration.seconds()
        return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
      }

      const hours = Math.floor(duration.asHours())
      const minutes = duration.minutes()
      const seconds = duration.seconds()
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    },
    onTouchStart(e) {
      this.dragStartY = this.getDragYPos(e)
      document.addEventListener(this.dragEvent, this.onDrag)
    },
    onDrag(e) {
      const dragY = this.getDragYPos(e)
      const dragDist = Math.abs(dragY - this.dragStartY)
      if (!this.isDragging && dragDist > this.dragThreshold) {
        this.isDragging = true
        this.isDraggingDown = dragY > this.dragStartY
        if (this.isDraggingDown) {
          const prevCard = this.stack[this.stack.length - 1]
          if (!prevCard) return
          this.ghostCard = {
            ...prevCard,
            ...this.cardDefaults[0],
            yPos: this.paddingVertical - this.cardHeight - this.overlapOffset,
            zIndex: 100,
          }
        }
      }
      if (this.isDragging) {
        this.moveStack(dragY)
      }
    },
    onTouchEnd() {
      if (this.isDragging) {
        this.updateStack()
      }
      this.isDragging = false
      this.ghostCard = null
      document.removeEventListener(this.dragEvent, this.onDrag)
    },
    getCardStyle(card) {
      return {
        display: card.display,
        width: card.width,
        height: `${card.height}px`,
        zIndex: card.zIndex,
        transition: `transform ${this.isDragging ? 0 : this.speed}s ease, opacity ${this.speed}s ease`,
        transform: `translate(0, ${card.yPos}px) scale(${card.scale})`,
        cursor: this.showClone ? "default" : "pointer",
      }
    },
    getCardBackground(card, cardIndex) {
      let bgStyle = {}
      if (this.exercises && card.exercise) {
        const imgUrl = this.getImageForExercise(card.exercise)
        if (imgUrl) {
          bgStyle.backgroundImage = `linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 50%, rgba(0,0,0,0.9) 100%), url('${imgUrl}')`
          bgStyle.backgroundSize = "cover"
          bgStyle.backgroundPosition = "center"
        } else {
          bgStyle.backgroundColor = `hsl(${cardIndex * 30}, 30%, 35%)`
        }
      } else {
        bgStyle.backgroundColor = card.backgroundColor || `hsl(${cardIndex * 50}, 40%, 40%)`
      }
      return bgStyle
    },
    getExercise(id) {
      const fromList = this.exerciseList.find((ex) => ex._id === id)
      if (fromList) return fromList

      // Fallback: Suche in den historischen Daten (hubGroupedExercises)
      // Das ist nötig, falls eine Übung gelöscht wurde oder nicht in der aktuellen Liste ist.
      if (this.hubGroupedExercises) {
        const fromHistory = this.hubGroupedExercises.find((group) => {
          const groupExId = group.exercise && group.exercise._id ? group.exercise._id : group.exercise
          return groupExId === id
        })
        if (fromHistory && fromHistory.exercise && typeof fromHistory.exercise === "object") {
          return fromHistory.exercise
        }
      }

      return null
    },
    getImageForExercise(ex) {
      if (!ex) return ""
      if (ex.picture) return this.getImageUrl(ex.picture)
      if (ex.manual && ex.manual[0]?.picture) return this.getImageUrl(ex.manual[0].picture)
      return ""
    },
    getImageUrl(id) {
      return "/img/uebungen/" + id.replace(/\.[^/.]+$/, "") + ".webp"
    },
    init() {
      this.stack = this.cards.map((card, i) => ({ _id: Date.now() + i, _index: i, ...card, ...this.cardDefaults[i] }))
    },
    rebuild() {
      this.$nextTick(() => {
        this.stack = this.stack.map((c, i) => ({ ...c, ...this.cardDefaults[i] }))
      })
    },
    handleResize: debounce(function () {
      this.width = this.$el.clientWidth
      this.rebuild()
    }, 250),
    expandCard(idx, event) {
      const orig = event.currentTarget
      const rect = orig.getBoundingClientRect()
      this.originalCloneStyle = {
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        borderRadius: getComputedStyle(orig).borderRadius,
      }
      this.expandedData = { ...this.stack[idx] }
      this.cloneStyle = {
        position: "fixed",
        top: this.originalCloneStyle.top,
        left: this.originalCloneStyle.left,
        width: this.originalCloneStyle.width,
        height: this.originalCloneStyle.height,
        zIndex: 10000,
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        transformOrigin: "center",
      }
      this.cloneCardStyle = {
        backgroundColor: "transparent",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: this.originalCloneStyle.borderRadius,
      }
      this.showClone = true
      this.$nextTick(() => {
        Object.assign(this.cloneStyle, { top: "0", left: "0", width: "100vw", height: "100vh" })
        this.cloneCardStyle.borderRadius = "0"
      })
    },
    closeClone() {
      this.isClosing = true
      this.cloneCardStyle.overflow = "hidden"
      Object.assign(this.cloneStyle, this.originalCloneStyle)
      this.cloneCardStyle.borderRadius = this.originalCloneStyle.borderRadius
    },
    onTransitionEnd() {
      if (this.isClosing) {
        this.showClone = false
        this.isClosing = false
        this.expandedData = {}
      } else if (this.showClone) {
        this.cloneCardStyle.overflow = "auto"
        this.$emit("expanded", this.expandedData)
      }
    },
    onNext() {
      this.stack.push(this.stack.shift())
      this.rebuild()
    },
    onPrevious() {
      const cardToMove = this.stack[this.stack.length - 1]
      if (!cardToMove) return
      cardToMove.yPos = this.paddingVertical - this.cardHeight - this.overlapOffset
      cardToMove.scale = 1
      cardToMove.opacity = 1
      this.stack.unshift(this.stack.pop())
      this.rebuild()
    },
    updateStack() {
      const minD = (this.cardHeight + this.paddingVertical) / (1 / this.sensitivity)
      let dist = 0
      if (this.isDraggingDown) {
        dist = this.lastDragOffset
      } else {
        const topCard = this.stack[this.activeCardIndex]
        if (!topCard) return this.rebuild()
        const restY = this.verticalStackRestPoints[this.activeCardIndex]
        dist = topCard.yPos - restY
      }
      if (this.isDraggingDown ? dist > minD : Math.abs(dist) > minD) {
        this.isDraggingDown ? this.onPrevious() : this.onNext()
      } else {
        this.rebuild()
      }
    },
    moveStack(dragY) {
      const off = dragY - this.dragStartY
      this.lastDragOffset = off
      this.$emit("move", off / (this.cardHeight + this.paddingVertical))
      if (this.isDraggingDown) {
        if (this.ghostCard) {
          const initialGhostY = this.paddingVertical - this.cardHeight - this.overlapOffset
          this.ghostCard.yPos = initialGhostY + off
        }
      } else {
        this.activeCardIndex = 0
        this.stack = this.stack.map((c, i) => ({
          ...c,
          ...this.cardDefaults[i],
          yPos: i === 0 ? this.cardDefaults[i].yPos + off : this.cardDefaults[i].yPos,
        }))
      }
    },
    getDragYPos(e) {
      return this.isTouch ? e.touches[0].clientY : e.clientY
    },
  },
}
</script>
<style scoped>
.best-card-icon.volume {
  background-color: rgba(129, 212, 250, 0.1); /* Light Blue */
  box-shadow: 0 0 15px rgba(129, 212, 250, 0.2);
}

/* NEU START */
.best-card-icon.e1rm {
  background-color: rgba(255, 138, 128, 0.1); /* Helles Rot */
  box-shadow: 0 0 15px rgba(255, 138, 128, 0.2);
}
/* NEU: Styles für den Supersatz-Avatar-Stapel */
.superset-avatar-stack {
  display: flex;
  align-items: center;
  padding-left: 8px; /* Platz für die Überlappung schaffen */
}
.superset-avatar-stack .v-avatar {
  border: 2px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}

.personal-best-card {
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 20px !important;
  padding: 18px !important;
  display: flex;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
  box-shadow: none !important;
}
.personal-best-card:hover {
  background: rgba(255, 255, 255, 0.05) !important;
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3) !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
}
.best-card-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  margin-right: 16px;
  flex-shrink: 0;
}
.best-card-icon.trophy {
  background-color: rgba(255, 215, 0, 0.1); /* Gold */
  box-shadow: 0 0 15px rgba(255, 215, 0, 0.2);
}
.best-card-icon.volume {
  background-color: rgba(129, 212, 250, 0.1); /* Light Blue */
  box-shadow: 0 0 15px rgba(129, 212, 250, 0.2);
}
.glass-card {
  background-color: rgba(30, 30, 30, 0.8) !important;
  backdrop-filter: blur(25px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(25px) saturate(180%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.text-on-image {
  text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.6), 0px 4px 12px rgba(0, 0, 0, 0.8);
}
.text-muted {
  color: rgba(255, 255, 255, 0.6) !important;
}
.card-clone {
  background-color: #121212;
  color: white;
  overflow: hidden;
}
.background-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 150vw;
  height: 150vh;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle at center, var(--glow-color, #555) 0%, transparent 45%);
  filter: blur(100px);
  opacity: 0.15;
  pointer-events: none;
  z-index: -1;
  animation: pulse-glow-subtle 8s infinite alternate;
}
@keyframes pulse-glow-subtle {
  from {
    opacity: 0.15;
    transform: translate(-50%, -50%) scale(1);
  }
  to {
    opacity: 0.3;
    transform: translate(-50%, -50%) scale(1.1);
  }
}
.hero-header {
  min-height: 250px;
  padding: 72px 24px 24px 24px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  background-size: cover;
  background-position: center center;
  position: relative;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
}
.hero-header:not(:has(.text-center)) .hero-content {
  align-self: flex-start;
}
.hero-content {
  animation: fade-in-up 0.8s 0.2s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
  opacity: 0;
  transform: translateY(30px);
  z-index: 2;
}
.content-wrapper {
  animation: fade-in-up 0.6s 0.2s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
  opacity: 0;
  transform: translateY(30px);
}
.chart-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  padding: 2px 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.chart-nav:hover {
  background: rgba(255, 255, 255, 0.1);
}
.chart-period-label {
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
}
.stat-card-small {
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 18px !important;
  padding: 16px 12px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
  box-shadow: none !important;
}
.stat-card-small:hover {
  background: rgba(255, 255, 255, 0.06) !important;
  transform: translateY(-3px);
  border-color: rgba(255, 255, 255, 0.18) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2) !important;
}
.stat-emoji-small {
  font-size: 1.8rem;
  margin-bottom: 8px;
}
.stat-value-small {
  font-size: 1.2rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}
.stat-label-small {
  font-size: 0.7rem;
  color: #bdbdbd;
}
.pr-card {
  background: linear-gradient(135deg, #1e1e1e 0%, #151515 100%) !important;
  border: 1.5px solid rgba(255, 215, 0, 0.25) !important;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.05) !important;
  border-radius: 18px !important;
  padding: 14px 18px !important;
  width: 210px !important;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
}
.pr-card:hover {
  border-color: #ffd700 !important;
  box-shadow: 0 6px 20px rgba(255, 215, 0, 0.25) !important;
  transform: translateY(-3px);
}
.pr-title {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: white;
}
.pr-type {
  font-size: 0.8rem;
  color: #bdbdbd;
}
.pr-values {
  margin-top: 8px;
  font-size: 1.2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
}
.old-value {
  color: #9e9e9e;
  text-decoration: line-through;
}
.new-value {
  font-weight: bold;
  color: #a5d6a7;
}
.custom-panels.v-expansion-panels {
  z-index: 0;
}
.custom-panels .v-expansion-panel {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 20px !important;
  margin-bottom: 12px !important;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
.custom-panels .v-expansion-panel:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}
.custom-panels .v-expansion-panel-header {
  font-weight: 700;
  padding: 16px 20px !important;
}
.custom-panels .v-expansion-panel-header::before {
  border-radius: 20px !important;
}
.v-expansion-panel-content__wrap {
  padding: 0 20px 20px !important;
}
.transparent-table,
.transparent-table th,
.transparent-table td {
  background-color: transparent !important;
  color: white !important;
}
.transparent-table th {
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.4) !important;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
  padding: 8px 12px !important;
}
.transparent-table td {
  font-size: 0.95rem !important;
  font-weight: 500;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
  padding: 10px 12px !important;
}
.transparent-table tr:hover td {
  background-color: rgba(255, 255, 255, 0.02) !important;
}
.verlauf-card-icon {
  position: absolute;
  top: 20px;
  right: 20px;
  opacity: 0.5;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
  transition: all 0.3s ease;
  pointer-events: none;
}
.vue-card-stack__wrapper {
  position: relative;
  touch-action: none;
}
.vue-card-stack__stack {
  position: relative;
  overflow: hidden;
  perspective: 1500px;
}
.vue-card-stack__card {
  position: absolute;
  transform-origin: center top;
  transition: all 0.3s ease-out;
  border-radius: 28px !important;
}
.card-content-wrapper {
  width: 100%;
  height: 100%;
  border-radius: 28px;
  position: relative;
  overflow: hidden;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.4s ease;
  will-change: transform, box-shadow;
}
.card-content-wrapper.is-top-card {
  box-shadow: 0 0 20px -8px var(--glow-color), 0 10px 20px rgba(0, 0, 0, 0.3);
  transform-style: preserve-3d;
}
.is-top-card .v-card-title,
.is-top-card .v-card-text {
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.vue-card-stack__card:first-child:hover .card-content-wrapper {
  transform: translateY(-10px) scale(1.05) rotateX(7deg) rotateY(3deg);
  box-shadow: 0 0 45px -10px var(--glow-color), 0 25px 40px rgba(0, 0, 0, 0.4);
}
.vue-card-stack__card:first-child:hover .v-card-title {
  transform: translateZ(30px);
}
.vue-card-stack__card:first-child:hover .v-card-text {
  transform: translateZ(20px);
}
.card-shine-effect::after {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(to right, rgba(255, 255, 255, 0) 30%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0) 70%);
  transform: translateX(-150%) rotate(25deg);
  transition: transform 1.2s cubic-bezier(0.2, 0.8, 0.2, 1);
  pointer-events: none;
}
.vue-card-stack__card:first-child:hover .card-shine-effect::after {
  transform: translateX(150%) rotate(25deg);
}
.clone-close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10001;
  background-color: rgba(0, 0, 0, 0.3);
}
.overflow-y-auto {
  overflow-y: auto;
}
.animated-item {
  opacity: 0;
  transform: translateY(20px);
  animation: fade-in-up 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  animation-delay: var(--delay);
}
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.delete-dialog-wrapper {
  background-color: transparent !important;
  box-shadow: none !important;
}

.delete-dialog-card {
  border-radius: 24px !important;
  padding: 16px;
  position: relative;
  overflow: hidden;
}

.delete-dialog-title {
  font-size: 1.4rem !important;
  font-weight: 700;
  word-break: normal;
}

.delete-dialog-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 200%;
  height: 200%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, var(--glow-color, #ff5252) 0%, transparent 40%);
  filter: blur(80px);
  opacity: 0.3;
  pointer-events: none;
  z-index: -1;
}

/* Erfolgs-Overlay */
.success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000; /* Muss über allem liegen */
}

.success-content {
  text-align: center;
  color: white;
}

.success-icon-wrapper {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: #4caf50; /* Erfolgs-Grün */
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 30px rgba(76, 175, 80, 0.7);
}

/* Animationen */
@keyframes scale-in {
  from {
    transform: scale(0.7);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.dialog-scale-enter-active {
  animation: scale-in 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.dialog-scale-leave-active {
  animation: scale-in 0.3s cubic-bezier(0.6, -0.28, 0.735, 0.045) reverse;
}

.success-pop-enter-active {
  animation: scale-in 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.success-pop-leave-active {
  transition: opacity 0.3s ease;
}
.success-pop-leave-to {
  opacity: 0;
}
.hyrox-summary-hero {
  min-height: 320px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-size: cover;
  background-position: center 30%;
  position: relative;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
}

.hyrox-title {
  font-size: 3rem;
  font-weight: 700;
  color: white;
  letter-spacing: 0.1em;
  text-shadow: 0 0 20px #ffed00, 0 0 40px rgba(255, 237, 0, 0.5);
  line-height: 1;
  animation: text-flicker 3s infinite alternate;
}

.total-time-display {
  font-family: monospace;
  font-size: 4rem;
  font-weight: bold;
  color: white;
  margin: 16px 0;
}

.hyrox-subtitle {
  font-size: 1.2rem;
  color: #aaa;
}

.segment-list .segment-item {
  background: rgba(255, 255, 255, 0.04);
  border-radius: 16px;
  margin-bottom: 4px;
  padding: 12px 16px !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}
.segment-list .segment-item:hover {
  border-color: #ffed00;
  background: rgba(255, 237, 0, 0.08);
}

.segment-title {
  font-weight: bold;
  font-size: 1rem;
}
.segment-time {
  font-family: monospace;
  font-size: 1.2rem;
  font-weight: bold;
  color: white;
}
.rotate-icon {
  transform: rotate(180deg);
}
.v-list-item__action .v-icon {
  transition: transform 0.3s ease-in-out;
}

.segment-history-wrapper {
  padding: 8px 0;
}
.segment-history-table {
  background-color: rgba(0, 0, 0, 0.3) !important;
  border-radius: 12px;
}
.segment-history-table th,
.segment-history-table td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.no-history-text {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
}

/* Keyframe für den Titel-Flicker-Effekt */
@keyframes text-flicker {
  0%,
  18%,
  22%,
  25%,
  53%,
  57%,
  100% {
    text-shadow: 0 0 10px #ffed00, 0 0 20px #ffed00, 0 0 30px #ffed00, 0 0 40px #ffed00;
  }
  20%,
  24%,
  55% {
    text-shadow: none;
  }
}
.hyrox-category-display {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 8px 20px;
  margin-top: 8px;
  font-size: 1.2rem;
  font-weight: bold;
  color: #cccccc;
  text-shadow: none;
  backdrop-filter: blur(5px);
}

.hyrox-category-display .v-icon {
  color: #ffed00; /* Das Icon bekommt die Highlight-Farbe */
  filter: drop-shadow(0 0 5px #ffed00);
}
</style>
